import { useDebounce } from '@fuse/hooks';
import { CircularProgress, Typography } from '@material-ui/core';
import ListSubheader from '@material-ui/core/ListSubheader';
import { makeStyles, useTheme } from '@material-ui/core/styles';
import TextField from '@material-ui/core/TextField';
import useMediaQuery from '@material-ui/core/useMediaQuery';
import Autocomplete from '@material-ui/lab/Autocomplete';
import clsx from 'clsx';
import React, { useState } from 'react';
import { VariableSizeList } from 'react-window';
import InfiniteLoader from 'react-window-infinite-loader';

const useStyles = makeStyles((theme) => ({
  listbox: {
    boxSizing: 'border-box',
    '& ul': {
      padding: 0,
      margin: 0,
    },
  },
  input: {
    '& .MuiOutlinedInput-root': {
      '& fieldset': {
        borderColor: '#707070',
      },
    },
  },
  root: {
    '&.warning fieldset': {
      borderColor: '#ff9234',
    },
    '&.warning > p, &.warning > label': {
      color: '#ff9234',
    },
  },
}));

function SelectAsyncInput({
  debug = false,
  multiple,
  id,
  label,
  required = false,
  fullWidth = true,
  autoFocus = false,
  disabled = false,
  autoSelect = false,
  fetchData = async () => {},
  onClearInput = () => {},
  onResetInput = () => {},
  onBlur,
  onChange,
  onFocus,
  onKeyDown,
  onKeyPress,
  onKeyUp,
  value,
  renderOption,
  hidePlaceholder,
  touched,
  error,
  warning,
  getOptionDisabled,
  disableClearable,
  getOptionLabel = (option) => option?.label,
  getOptionKey = (option) => option?.key,
  style,
  fetchOnFocus,
  setSearchData,
  helperText,
}) {
  const classes = useStyles();

  const [loading, setLoading] = useState(false);
  const [open, setOpen] = useState(false);
  const [options, setOptions] = useState([]);
  const [nextPage, setNextPage] = useState(0);
  const [, setTotalItems] = useState(0);
  const [inputValue, setInputValue] = useState('');

  const handleFilter = useDebounce(
    (value) => {
      setOptions([]);
      setLoading(true);
      setOpen(true);
      (async () => {
        const response = await fetchData(value);
        setInputValue(value);
        setOptions(response.data);
        setNextPage(response.nextPage);
        setTotalItems(response.totalItems);
        setLoading(false);
        if (setSearchData) {
          setSearchData(response.data);
        }
      })();
    },
    300,
    {}
  );

  return (
    <Autocomplete
      multiple={multiple}
      style={style}
      classes={classes}
      debug={debug}
      autoSelect={autoSelect}
      disabled={disabled}
      closeText="Fechar"
      openText="Abrir"
      noOptionsText="Nenhum registro encontrado"
      loadingText="Carregando..."
      clearText="Limpar"
      disableListWrap
      disableClearable={disableClearable ? disableClearable : !getOptionKey(value)}
      id={id}
      name={id}
      value={value || null}
      options={options}
      loading={loading}
      open={open}
      onOpen={(event) => {
        if (fetchOnFocus) {
          handleFilter(value || '');
        }
      }}
      onClose={(event, reason) => {
        setOpen(false);
        setOptions([]);
        setInputValue('');
        setNextPage(0);
        setTotalItems(0);
      }}
      onInputChange={(event, value, reason) => {
        switch (reason) {
          case 'reset':
            onResetInput();
            break;
          case 'clear':
            onClearInput();
            break;
          case 'input':
            handleFilter(value);
            break;
          default:
            break;
        }
      }}
      renderInput={(params) => {
        return (
          <TextField
            {...params}
            placeholder={!hidePlaceholder && 'Digite para pesquisar...'}
            className={clsx(classes.root, warning && 'warning', classes.input)}
            label={label}
            required={required}
            fullWidth={fullWidth}
            autoFocus={autoFocus}
            variant="outlined"
            InputLabelProps={{ shrink: true }}
            InputProps={{
              ...params.InputProps,
              endAdornment: (
                <>
                  {loading ? <CircularProgress size={20} /> : null}
                  {params.InputProps.endAdornment}
                </>
              ),
            }}
            error={error && touched}
            helperText={helperText ? helperText : warning || (touched && error)}
            onFocus={onFocus}
            onBlur={onBlur}
            onKeyDown={onKeyDown}
            onKeyPress={onKeyPress}
            onKeyUp={onKeyUp}
          />
        );
      }}
      onChange={(event, value) => {
        onChange(event, value);
      }}
      filterOptions={(options, state) => {
        return options;
      }}
      getOptionSelected={(option, value) => {
        return getOptionKey(option) === getOptionKey(value);
      }}
      getOptionDisabled={getOptionDisabled}
      getOptionLabel={(option) => {
        return getOptionLabel(option) || '';
      }}
      renderOption={(option) => {
        return (renderOption && renderOption(option)) || <Typography noWrap>{getOptionLabel(option)}</Typography>;
      }}
      ListboxProps={{
        items: options,
        hasNextPage: nextPage && nextPage > 0,
        isNextPageLoading: loading,
        loadNextPage: async () => {
          setLoading(true);
          const response = await fetchData(inputValue, nextPage);
          setOptions([...options, ...response.data]);
          setNextPage(response.nextPage);
          setLoading(false);
        },
      }}
      ListboxComponent={ListboxComponent}
    />
  );
}

const LISTBOX_PADDING = 8; // px

const OuterElementContext = React.createContext({});

const OuterElementType = React.forwardRef((props, ref) => {
  const outerProps = React.useContext(OuterElementContext);
  return <div ref={ref} {...props} {...outerProps} />;
});

const ListboxComponent = React.forwardRef(function ListboxComponent({ children, ...props }, ref) {
  const theme = useTheme();
  const smUp = useMediaQuery(theme.breakpoints.up('sm'), { noSsr: true });
  const itemSize = smUp ? 36 : 48;

  const getChildSize = (child) => {
    if (React.isValidElement(child) && child.type === ListSubheader) {
      return 48;
    }
    return itemSize;
  };

  const getHeight = () => {
    if (itemCount > 8) {
      return 8 * itemSize;
    }
    return itemData.map(getChildSize).reduce((a, b) => a + b, 0);
  };

  const itemData = React.Children.toArray(children);

  // If there are more items to be loaded then add an extra row to hold a loading indicator.
  const itemCount = props.hasNextPage ? props.items.length + 1 : props.items.length;

  // Only load 1 page of items at a time.
  // Pass an empty callback to InfiniteLoader in case it asks us to load more than once.
  const loadMoreItems = props.loadNextPage;

  // Every row is loaded except for our loading indicator row.
  const isItemLoaded = (index) => !props.hasNextPage || index < props.items.length;

  const renderRow = ({ data, index, style }) => {
    if (!isItemLoaded(index)) {
      return <div style={{ ...style, top: style.top + LISTBOX_PADDING }} />;
    }

    return React.cloneElement(data[index], {
      style: {
        ...style,
        top: style.top + LISTBOX_PADDING,
      },
    });
  };

  return (
    <div ref={ref}>
      <OuterElementContext.Provider value={props}>
        <InfiniteLoader isItemLoaded={isItemLoaded} itemCount={itemCount} loadMoreItems={loadMoreItems}>
          {({ onItemsRendered, ref }) => {
            return (
              <VariableSizeList
                ref={ref}
                outerElementType={OuterElementType}
                innerElementType="ul"
                width="100%"
                height={getHeight() + 2 * LISTBOX_PADDING}
                itemData={itemData}
                itemSize={(index) => getChildSize(itemData[index])}
                itemCount={itemCount}
                onItemsRendered={onItemsRendered}
                overscanCount={5}
              >
                {renderRow}
              </VariableSizeList>
            );
          }}
        </InfiniteLoader>
      </OuterElementContext.Provider>
    </div>
  );
});

export default SelectAsyncInput;
